/**
 * captureLabel.js — pure function that renders a chat-capture result's
 * display_payload into a one-line, natural-sentence chip label.
 *
 * Called at render time in Home.js (and D.5's My Captures). Pure; never
 * throws; never emits snake_case predicate names verbatim.
 *
 * Contract:
 *   formatChipLabel({ entity_type, entity_name, predicate, value }) → string
 *
 * Handles all 8 Rule 102 predicates with schema-aware templates and
 * defensive fallbacks on missing/malformed value fields. A final guard
 * rejects any output containing literal "undefined" / "null" / "NaN" /
 * "[object" (e.g. from an unanticipated template-interpolation bug) in
 * favor of the universal "Captured intelligence" string.
 *
 * Subject composition rule:
 *   entity_name + value.item  → "{entity} {item}"   (e.g. "Parker O-ring")
 *   entity_name only          → "{entity}"
 *   value.item only           → "{item}"
 *   neither                   → no prefix; start with the predicate phrase
 *
 * Percentage sign: word-based ("up 6.0%" / "down 20.0%") — direction carries
 * the sign. Magnitudes are abs() then .toFixed(1).
 *
 * Currency: the project's house-style Intl.NumberFormat USD (0 fraction
 * digits) — matches Accounts.js, AwardAssignment.js, BidEntry.js, Analytics.js.
 *
 * Durations: lead time in weeks ("{N} weeks"); " → " for ranges.
 */


const UNIVERSAL_FALLBACK = 'Captured intelligence';

const PREDICATE_LABEL = {
  price_change: 'price change',
  lead_time_change: 'lead time change',
  capacity_constraint: 'capacity constraint',
  sourcing_strategy_change: 'sourcing strategy change',
  demand_signal: 'demand signal',
  quality_issue: 'quality issue reported',
  contact_change: 'contact change',
  material_substitution: 'material substitution',
};

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});


function trimOrNull(v) {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

function formatPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${Math.abs(n).toFixed(1)}%`;
}

function composeSubject(entityName, item) {
  const e = trimOrNull(entityName);
  const i = trimOrNull(item);
  if (e && i) return `${e} ${i}`;
  if (e) return e;
  if (i) return i;
  return null;
}

function prefix(subject) {
  return subject ? `${subject} · ` : '';
}

function predicateFallback(predicate, entityName, item) {
  const subject = composeSubject(entityName, item);
  const phrase = PREDICATE_LABEL[predicate] || null;
  if (!phrase) return UNIVERSAL_FALLBACK;
  return subject ? `${subject} · ${phrase}` : phrase;
}


function renderPriceChange(v, entityName) {
  const subject = composeSubject(entityName, v.item);
  const pct = formatPct(v.amount_pct);
  const dir = v.direction === 'up' || v.direction === 'down' ? v.direction : null;
  if (pct && dir) {
    const timing = trimOrNull(v.timing);
    return `${prefix(subject)}price ${dir} ${pct}${timing ? ` ${timing}` : ''}`;
  }
  // Defensive: Haiku may emit absolute USD amounts instead of a percentage.
  const amtUsd = Number(v.amount_usd);
  if (Number.isFinite(amtUsd) && dir) {
    return `${prefix(subject)}price ${dir} ${currencyFmt.format(Math.abs(amtUsd))}`;
  }
  return predicateFallback('price_change', entityName, v.item);
}

function renderLeadTimeChange(v, entityName) {
  const subject = composeSubject(entityName, v.item);
  const fromWeeks = Number(v.from_weeks ?? v.from);
  const toWeeks = Number(v.to_weeks ?? v.to);
  if (Number.isFinite(fromWeeks) && Number.isFinite(toWeeks)) {
    return `${prefix(subject)}lead time ${fromWeeks} → ${toWeeks} weeks`;
  }
  if (v.direction === 'up') return `${prefix(subject)}lead time extended`;
  if (v.direction === 'down') return `${prefix(subject)}lead time shortened`;
  return predicateFallback('lead_time_change', entityName, v.item);
}

function renderCapacityConstraint(v, entityName) {
  const subject = composeSubject(entityName, v.item);
  const pct = formatPct(v.fill_rate_pct);
  if (pct) return `${prefix(subject)}${pct} fill rate`;
  const ctype = trimOrNull(v.constraint_type);
  if (ctype) return `${prefix(subject)}${ctype}`;
  return predicateFallback('capacity_constraint', entityName, v.item);
}

function renderSourcingStrategy(v, entityName) {
  const subject = composeSubject(entityName, null);
  const from = trimOrNull(v.from);
  const to = trimOrNull(v.to);
  if (from && to) {
    const added = trimOrNull(v.added_supplier);
    return `${prefix(subject)}${from} → ${to}${added ? ` (adding ${added})` : ''}`;
  }
  return predicateFallback('sourcing_strategy_change', entityName, null);
}

function renderDemandSignal(v, entityName) {
  const subject = composeSubject(entityName, null);
  const pct = formatPct(v.magnitude_pct);
  const dir = v.direction === 'up' || v.direction === 'down' ? v.direction : null;
  if (pct && dir) {
    const timeframe = trimOrNull(v.timeframe);
    return `${prefix(subject)}demand ${dir} ${pct}${timeframe ? ` ${timeframe}` : ''}`;
  }
  return predicateFallback('demand_signal', entityName, null);
}

function renderQualityIssue(v, entityName) {
  const subject = composeSubject(entityName, v.item);
  const pct = formatPct(v.defect_pct);
  if (pct) return `${prefix(subject)}${pct} out of spec`;
  if (trimOrNull(v.lot)) return `${prefix(subject)}bad lot`;
  return predicateFallback('quality_issue', entityName, v.item);
}

function renderContactChange(v, entityName) {
  const subject = composeSubject(entityName, null);
  const oldC = trimOrNull(v.old_contact);
  const newC = trimOrNull(v.new_contact);
  // Preserve Haiku's capitalization on role — no normalization.
  const roleRaw = trimOrNull(v.role);
  const role = roleRaw ? ` (${roleRaw})` : '';
  if (oldC && newC) return `${prefix(subject)}${oldC} → ${newC}${role}`;
  if (newC) return `${prefix(subject)}new contact ${newC}${role}`;
  if (oldC) return `${prefix(subject)}${oldC} departed`;
  return predicateFallback('contact_change', entityName, null);
}

function renderMaterialSubstitution(v, entityName) {
  const subject = composeSubject(entityName, null);
  const from = trimOrNull(v.from);
  const to = trimOrNull(v.to);
  if (from && to) return `${prefix(subject)}${from} → ${to}`;
  return predicateFallback('material_substitution', entityName, null);
}


const RENDERERS = {
  price_change: renderPriceChange,
  lead_time_change: renderLeadTimeChange,
  capacity_constraint: renderCapacityConstraint,
  sourcing_strategy_change: renderSourcingStrategy,
  demand_signal: renderDemandSignal,
  quality_issue: renderQualityIssue,
  contact_change: renderContactChange,
  material_substitution: renderMaterialSubstitution,
};


// Final guard — catches any template-interpolation bug that would leak
// "undefined" / "null" / "NaN" / "[object Object]" into the UI. Word
// boundaries prevent false positives on legitimate substrings (e.g. a
// hypothetical entity name containing "null" as part of a longer word).
function guard(s) {
  if (typeof s !== 'string' || s.length === 0) return UNIVERSAL_FALLBACK;
  if (/\b(undefined|null|NaN)\b|\[object/.test(s)) return UNIVERSAL_FALLBACK;
  return s;
}


export function formatChipLabel(displayPayload) {
  try {
    if (!displayPayload || typeof displayPayload !== 'object') {
      return UNIVERSAL_FALLBACK;
    }
    const { entity_name: entityName, predicate, value } = displayPayload;
    const v = value && typeof value === 'object' ? value : {};
    const renderer = RENDERERS[predicate];
    if (renderer) return guard(renderer(v, entityName));
    // Unknown / future predicate — defensive prose fallback.
    return guard(predicateFallback(predicate, entityName, null));
  } catch (_) {
    return UNIVERSAL_FALLBACK;
  }
}


// D.7 provenance chip label. The backend assertions_cited[] entries lack the
// rich `value` blob that drives formatChipLabel's per-predicate templates,
// so this renders the predicateFallback shape: "{entity} · {predicate phrase}".
export function formatAssertionChipLabel({ entity_name, predicate }) {
  return guard(predicateFallback(predicate, entity_name, null));
}
